import './ClassProductTable.scss'

import { EditOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Checkbox, Image, message, Modal, Tabs } from 'antd'
import PurchaseTag from 'components/purchase-tag/PurchaseTag'
import { SinifUrunu } from 'gql/GeneratedGraphqlTypes'
import { getPurchaseStatus } from 'helpers/PurchaseStatus'
import React, { useState } from 'react'
import { ETIKET_URUN_ID, KITAP_KAPLAMA_URUN_ID } from 'utils/const'
import { getEffectiveMaxAdet, setPriceFormat } from 'utils/helpers'

import { SecilenUrun } from '../../pages/class-products/ClassProductsPage'
import ClassProductsModal from '../class-product-modal/ClassProductsModal'
import { SchoolClothesPage } from './school-clothes-page/SchoolClothesPage'
import { ReactComponent as ClassIcon } from './svg/class.svg'
import { ReactComponent as SchoolIcon } from './svg/school.svg'

interface ClassProductTableProps {
  products: SinifUrunu[]
  grupluUrunler?: SinifUrunu[]
  selectedProducts: SecilenUrun[]
  setSelectedProducts: React.Dispatch<React.SetStateAction<SecilenUrun[]>>
  lessonGruping: Boolean
  indirimliFiyatGosterilsinmi: Boolean
  activeTabKey?: string
  onTabChange?: (key: string) => void
}

export const ClassProductTable: React.FC<ClassProductTableProps> = ({
  products,
  grupluUrunler,
  selectedProducts,
  setSelectedProducts,
  lessonGruping,
  indirimliFiyatGosterilsinmi,
  activeTabKey,
  onTabChange,
}) => {
  const [quantities, setQuantities] = useState<Record<string, number>>({})
  const [modalVisible, setModalVisible] = useState(false)
  const [variantModalVisible, setVariantModalVisible] = useState(false)
  const [variantGroupProducts, setVariantGroupProducts] = useState<SinifUrunu[]>([])
  const isMobile = window.innerWidth <= 768

  const updateQuantity = (productId: string, dersId: number, newQuantity: number) => {
    setSelectedProducts((prev) =>
      prev.map((p) => (p.urunId === parseInt(productId) && p.dersId === dersId ? { ...p, adet: newQuantity } : p)),
    )
  }
  const handleQuantityChange = (product: SinifUrunu, delta: number) => {
    const productId = String(product.urun?.id)
    const dersId = product.ders?.id
    const current = selectedProducts.find((p) => p.urunId === product.urun?.id && p.dersId === dersId)?.adet
      ?? quantities[productId]
      ?? product.adet
      ?? 0
    const maxAdet = getEffectiveMaxAdet(product.urun?.bilgiler, product.max_adet)
    if (current + delta > maxAdet) {
      message.warning('Bu üründe adet sınırı uygulanmaktadır. Daha fazla satın alınamaz.')
    }
    const newQuantity = Math.min(Math.max(current + delta, 1), maxAdet)

    if (selectedProducts.some((p) => p.urunId === product.urun?.id && p.dersId === dersId)) {
      updateQuantity(productId, dersId!, newQuantity)
    } else {
      const yeniSecim: SecilenUrun = {
        isim: product.urun?.isim ?? '',
        urunId: product.urun_id,
        dersId: product.ders?.id!,
        adet: newQuantity,
        fiyat: product.indirimli_birim_fiyat ? product.indirimli_birim_fiyat : '',
        minAdet: product.min_adet ?? 0,
        maxAdet: getEffectiveMaxAdet(product.urun?.bilgiler, product.max_adet),
        grupId: null,
        zorunlulukGrubu: product.zorunluluk_grubu ?? 0,
        etiket_bilgisi: null,
        secilenBeden: null,
        kitapKaplama: product.urun?.kitap_kaplama ?? 0,
        kisa_aciklama: product.urun?.kisa_aciklama ? product.urun?.kisa_aciklama : '',
        grup: product.urun?.grup!,
        resimler: [...(product.urun?.resimler ?? [])],
      }
      setSelectedProducts((prev) => [...prev, yeniSecim])
    }
    setQuantities((prev) => ({ ...prev, [productId]: newQuantity }))
  }

  const handleSelectProduct = (product: SinifUrunu, checked: boolean) => {
    const dersId = product.ders?.id!
    const urunId = product.urun_id
    if (!checked && (product.min_adet ?? 0) > 0) return
    if (checked) {
      setSelectedProducts((prev) => {
        // Zaten seçiliyse tekrar ekleme (etiket_bilgisi gibi bilgileri koru)
        if (prev.some((p) => p.urunId === urunId && p.dersId === dersId)) return prev
        const yeniSecim: SecilenUrun = {
          isim: product.urun?.isim ?? '',
          urunId,
          dersId,
          adet: quantities[String(urunId)] || product.adet || 1,
          fiyat: product.indirimli_birim_fiyat!,
          minAdet: product.min_adet ?? 0,
        maxAdet: getEffectiveMaxAdet(product.urun?.bilgiler, product.max_adet),
          grupId: null,
          zorunlulukGrubu: product.zorunluluk_grubu ?? 0,
          etiket_bilgisi: null,
          secilenBeden: null,
          kitapKaplama: product.urun?.kitap_kaplama ?? 0,
          kisa_aciklama: product.urun?.kisa_aciklama ? product.urun?.kisa_aciklama : '',
          grup: product.urun?.grup!,
          resimler: [...(product.urun?.resimler ?? [])],
        }
        return [...prev, yeniSecim]
      })
    } else {
      setSelectedProducts((prev) => prev.filter((p) => !(p.urunId === urunId && p.dersId === dersId)))
    }
  }

  // Bir ders içindeki ürünleri grup.id'ye göre gruplar.
  // Aynı grup.id'ye sahip 2+ ürün varsa "group" olarak döner, tek ürünlüler "single" kalır.
  type GroupedItem =
    | { type: 'single'; product: SinifUrunu }
    | { type: 'group'; grupId: number; products: SinifUrunu[] }

  const makeGroupedItems = (dersUrunleri: SinifUrunu[]): GroupedItem[] => {
    const map = new Map<number, SinifUrunu[]>()
    const noGrup: SinifUrunu[] = []
    for (const p of dersUrunleri) {
      const gid = p.urun?.grup?.id
      if (gid == null) { noGrup.push(p); continue }
      if (!map.has(gid)) map.set(gid, [])
      map.get(gid)!.push(p)
    }
    const result: GroupedItem[] = []
    Array.from(map.entries()).forEach(([gid, prods]) => {
      if (prods.length > 1) result.push({ type: 'group', grupId: gid, products: prods })
      else result.push({ type: 'single', product: prods[0] })
    })
    for (const p of noGrup) result.push({ type: 'single', product: p })
    return result
  }

  const handleGroupVariantSelect = (product: SinifUrunu) => {
    const grupId = product.urun?.grup?.id ?? null
    const dersId = product.ders?.id!
    const groupUrunIds = new Set(variantGroupProducts.map((p) => p.urun_id))
    setSelectedProducts((prev) => [
      ...prev.filter((sp) =>
        !(sp.grupId === grupId && sp.dersId === dersId) &&
        !(sp.grupId === null && groupUrunIds.has(sp.urunId) && sp.dersId === dersId)
      ),
      {
        isim: product.urun?.grup?.isim ?? product.urun?.isim ?? '',
        urunId: product.urun_id,
        dersId,
        adet: quantities[String(product.urun_id)] || product.adet || 1,
        fiyat: product.indirimli_birim_fiyat ?? '',
        indirimsiz_birim_fiyat: product.indirimsiz_birim_fiyat ?? '',
        indirimli_birim_fiyat: product.indirimli_birim_fiyat ?? '',
        minAdet: product.min_adet ?? 0,
        maxAdet: getEffectiveMaxAdet(product.urun?.bilgiler, product.max_adet),
        grupId,
        zorunlulukGrubu: product.zorunluluk_grubu ?? 0,
        etiket_bilgisi: null,
        secilenBeden: product.urun?.bilgiler?.find((b) => b?.isim === 'Beden')?.deger ?? product.urun?.isim ?? '',
        kitapKaplama: product.urun?.kitap_kaplama ?? 0,
        kisa_aciklama: product.urun?.kisa_aciklama ?? '',
        grup: product.urun?.grup!,
        resimler: [...(product.urun?.resimler ?? [])],
      } as SecilenUrun,
    ])
    setVariantModalVisible(false)
  }

  const handleGroupDeselect = (grupId: number, dersId: number) => {
    setSelectedProducts((prev) => prev.filter((sp) => !(sp.grupId === grupId && sp.dersId === dersId)))
  }

  const groupedByDers = products.reduce((acc, product) => {
    const dersIsim = product.ders?.isim || 'Diğer'
    if (!acc[dersIsim]) acc[dersIsim] = []
    acc[dersIsim].push(product)
    return acc
  }, {} as Record<string, SinifUrunu[]>)

  const collapseItems = Object.entries(groupedByDers)
    .sort(([, aUrunler], [, bUrunler]) => {
      const aSira = parseInt(aUrunler[0]?.ders?.ders_sira ?? '9999', 10)
      const bSira = parseInt(bUrunler[0]?.ders?.ders_sira ?? '9999', 10)
      return aSira - bSira
    })
    .map(([dersIsim, dersUrunleri], index) => {
    const dersRenk = dersUrunleri[0].ders?.renk || '#666666'
    return {
    key: `${index}`,
    dersIsim,
    dersRenk,
    content: (
      <div className="collapse-content">
        {makeGroupedItems(dersUrunleri).map((item) => {
          if (item.type === 'group') {
            const { grupId, products: grpProducts } = item
            const dersId = grpProducts[0].ders?.id!
            const selected = selectedProducts.find((sp) => sp.grupId === grupId && sp.dersId === dersId)
            const selectedProduct = selected
              ? grpProducts.find((p) => p.urun_id === selected.urunId)
              : null
            const value = selected?.adet || 1
            const grupImage = grpProducts[0].urun?.grup?.resimler?.[0] || grpProducts[0].urun?.resimler?.[0] || ''
            const grupIsim = grpProducts[0].urun?.grup?.isim || grpProducts[0].urun?.isim || ''
            const priceSrc = selectedProduct ?? grpProducts[0]
            const priceValue = selected ? value : 1
            return (
              <div className="collapse-content-container" key={`grup_${grupId}`}>
                <div className="collapse-left">
                  <Image src={grupImage} alt="urun" width={isMobile ? 48 : 72} height={isMobile ? 48 : 72} />
                  <div className="collapse-content-info">
                    <span className="collapse-content-title">{grupIsim}</span>
                    {selected && (
                      <span className="collapse-content-note">{selected.secilenBeden}</span>
                    )}
                    <PurchaseTag status={getPurchaseStatus(grpProducts)} style={{ marginTop: 4 }} />
                  </div>
                </div>
                <Button
                  className="etiket-btn"
                  type="primary"
                  onClick={() => {
                    const sorted = [...grpProducts].sort((a, b) => {
                      const bA = parseFloat(a.urun?.bilgiler?.find((bi) => bi?.isim === 'Beden')?.deger ?? 'NaN')
                      const bB = parseFloat(b.urun?.bilgiler?.find((bi) => bi?.isim === 'Beden')?.deger ?? 'NaN')
                      if (!isNaN(bA) && !isNaN(bB)) return bA - bB
                      return 0
                    })
                    setVariantGroupProducts(sorted); setVariantModalVisible(true)
                  }}
                >
                  {selected ? <><EditOutlined /> Değiştir</> : 'Seçim Yap'}
                </Button>
                <div className="collapse-right">
                  <div className="quantity-div">
                    {selected && (
                      <>
                        <Button onClick={() => {
                          if (value <= 1) { handleGroupDeselect(grupId, dersId); return }
                          setSelectedProducts((prev) => prev.map((sp) =>
                            sp.grupId === grupId && sp.dersId === dersId ? { ...sp, adet: value - 1 } : sp
                          ))
                        }}>
                          <MinusOutlined />
                        </Button>
                        <span className="quantity-number">{value}</span>
                        <Button onClick={() => {
                          const maxAdet = getEffectiveMaxAdet(priceSrc.urun?.bilgiler, priceSrc.max_adet)
                          if (value < maxAdet) {
                            setSelectedProducts((prev) => prev.map((sp) =>
                              sp.grupId === grupId && sp.dersId === dersId ? { ...sp, adet: value + 1 } : sp
                            ))
                          } else {
                            message.warning('Bu üründe adet sınırı uygulanmaktadır. Daha fazla satın alınamaz.')
                          }
                        }}>
                          <PlusOutlined />
                        </Button>
                      </>
                    )}
                  </div>
                  <span className="price-span">
                    {indirimliFiyatGosterilsinmi &&
                      parseFloat(priceSrc.indirimsiz_birim_fiyat!) > parseFloat(priceSrc.indirimli_birim_fiyat!) && (
                        <><s>{setPriceFormat(String(priceSrc.indirimsiz_birim_fiyat), priceValue)} TL</s><br /></>
                      )}
                    {setPriceFormat(String(priceSrc.indirimli_birim_fiyat), priceValue)} TL
                  </span>
                  <Checkbox
                    checked={!!selected}
                    onChange={(e) => {
                      if (!e.target.checked) handleGroupDeselect(grupId, dersId)
                      else {
                        const sorted = [...grpProducts].sort((a, b) => {
                          const bA = parseFloat(a.urun?.bilgiler?.find((bi) => bi?.isim === 'Beden')?.deger ?? 'NaN')
                          const bB = parseFloat(b.urun?.bilgiler?.find((bi) => bi?.isim === 'Beden')?.deger ?? 'NaN')
                          if (!isNaN(bA) && !isNaN(bB)) return bA - bB
                          return 0
                        })
                        setVariantGroupProducts(sorted); setVariantModalVisible(true)
                      }
                    }}
                  />
                </div>
              </div>
            )
          }

          // single product (mevcut davranış)
          const product = item.product
          let value =
            selectedProducts.find((sp) => sp.urunId === product.urun_id && sp.dersId === product.ders?.id)?.adet ||
            quantities[String(product.urun_id)] ||
            product.adet ||
            1
          if (product.urun_id === KITAP_KAPLAMA_URUN_ID) {
            value = selectedProducts.reduce((acc, i) => acc + (i.kitapKaplama ?? 0) * (i.adet ?? 0), 0)
          }
          return (
            <div className="collapse-content-container" key={product.urun?.id}>
              <div className="collapse-left">
                <Image
                  src={product.urun?.resimler?.[0] || product.urun?.resim || ''}
                  alt="urun"
                  width={isMobile ? 48 : 72}
                  height={isMobile ? 48 : 72}
                />
                <div className="collapse-content-info">
                  <span className="collapse-content-title">{product.urun?.isim}</span>
                  <PurchaseTag status={getPurchaseStatus([product])} />
                  {product.sinif_liste_notu && (
                    <span
                      className="collapse-content-note"
                      dangerouslySetInnerHTML={{ __html: product.sinif_liste_notu }}
                    />
                  )}
                  {product.urun_id === ETIKET_URUN_ID && (() => {
                    const bilgi = selectedProducts.find((sp) => sp.urunId === ETIKET_URUN_ID)?.etiket_bilgisi
                    if (!bilgi) return null
                    const [ad, soyad, numara, sinif] = bilgi.split('|')
                    const parts = [ad && soyad ? `${ad} ${soyad}` : null, sinif || null, numara || null].filter(Boolean)
                    return <span className="collapse-content-note">{parts.join(' / ')}</span>
                  })()}
                </div>
              </div>
              {product.urun_id === ETIKET_URUN_ID ? (
                <Button className="etiket-btn" type="primary" onClick={() => {
                  handleSelectProduct(product, true)
                  setModalVisible(true)
                }}>
                  {selectedProducts.find((sp) => sp.urunId === ETIKET_URUN_ID)?.etiket_bilgisi
                    ? 'Etiketi Düzenle'
                    : 'Etiket Oluştur'}
                </Button>
              ) : (
                <div className="etiket-btn-placeholder" />
              )}
              <div className="collapse-right">
                <div className="quantity-div">
                  {product.urun_id !== KITAP_KAPLAMA_URUN_ID && (
                    <>
                      <Button onClick={() => handleQuantityChange(product, -1)}>
                        <MinusOutlined />
                      </Button>
                      <span className="quantity-number">{value}</span>
                      <Button onClick={() => handleQuantityChange(product, 1)}>
                        <PlusOutlined />
                      </Button>
                    </>
                  )}
                  {product.urun_id === KITAP_KAPLAMA_URUN_ID && (
                    <div className="kaplama-adet">
                      <span className="kaplama-adet-sayi">{value}</span>
                      <span className="kaplama-adet-label">kitap kaplanır</span>
                    </div>
                  )}
                </div>
                <span className="price-span">
                  {indirimliFiyatGosterilsinmi &&
                    parseFloat(product.indirimsiz_birim_fiyat!) > parseFloat(product.indirimli_birim_fiyat!) && (
                      <><s>{setPriceFormat(String(product.indirimsiz_birim_fiyat), value)} TL</s><br /></>
                    )}
                  {setPriceFormat(String(product.indirimli_birim_fiyat), value)} TL
                </span>
                <Checkbox
                  checked={selectedProducts.some(
                    (sp) => sp.urunId === product.urun_id && sp.dersId === product.ders?.id,
                  )}
                  disabled={(product.min_adet ?? 0) > 0}
                  onChange={(e) => handleSelectProduct(product, e.target.checked)}
                />
              </div>
            </div>
          )
        })}
      </div>
    ),
  }})

  const allGroupedItems = Object.values(groupedByDers).flatMap((dersUrunleri) => makeGroupedItems(dersUrunleri))
  const allSingleItems = allGroupedItems.filter((i) => i.type === 'single').map((i) => (i as { type: 'single'; product: SinifUrunu }).product)
  const isAllSelected =
    allSingleItems.length > 0 &&
    allSingleItems.every((p) =>
      selectedProducts.some((sp) => sp.urunId === p.urun_id && sp.dersId === p.ders?.id),
    )

  const handleToggleSelectAll = () => {
    if (isAllSelected) {
      // Sadece kirtasiye ürünlerini kaldır; tekstil seçimleri (grupId !== null) korunur
      setSelectedProducts((prev) => prev.filter((p) => p.grupId !== null || p.minAdet > 0))
    } else {
      // Sadece tek ürünleri seç; gruplu ürünler modal ile seçilmeli
      setSelectedProducts((prev) => {
        const existingUrunIds = new Set(prev.filter((p) => p.grupId === null).map((p) => `${p.urunId}_${p.dersId}`))
        const yeniUrunler: SecilenUrun[] = allSingleItems
          .filter((product) => !existingUrunIds.has(`${product.urun_id}_${product.ders?.id}`))
          .map((product) => ({
            isim: product.urun?.isim ?? '',
            urunId: product.urun_id,
            dersId: product.ders?.id!,
            grupId: null,
            adet: quantities[String(product.urun_id)] || product.adet || 1,
            resimler: [...(product.urun?.resimler ?? [])],
            kisa_aciklama: product.urun?.kisa_aciklama ?? '',
            indirimsiz_birim_fiyat: product.indirimsiz_birim_fiyat ?? '',
            indirimli_birim_fiyat: product.indirimli_birim_fiyat ?? '',
            fiyat: product.indirimli_birim_fiyat ?? '',
            minAdet: product.min_adet ?? 0,
        maxAdet: getEffectiveMaxAdet(product.urun?.bilgiler, product.max_adet),
            zorunlulukGrubu: product.zorunluluk_grubu ?? 0,
            etiket_bilgisi: null,
            secilenBeden: null,
            grup: product.urun?.grup!,
            kitapKaplama: product.urun?.kitap_kaplama ?? 0,
          }))
        return [...prev, ...yeniUrunler]
      })
    }
  }

  // "kitap" kelimesi kategori isminde neredeyse hiç geçmiyor (örn. "Sözlük - Atlas - Yazım Kılavuzu"),
  // bu yüzden gerçek kitap ürünlerini kitap_kaplama adedi (>0) ile de tespit ediyoruz.
  const kirtasiyeTabLabel = products.some(
    (p) => (p.urun?.kitap_kaplama ?? 0) > 0 || p.urun?.kategori?.isim?.toLowerCase().includes('kitap')
  ) ? 'KİTAP-KIRTASİYE' : 'KIRTASİYE'

  return (
    <div className="class-need-list-container">
      <Tabs
        defaultActiveKey="1"
        activeKey={activeTabKey}
        onChange={onTabChange}
        className={(() => { const hasTextil = !!(grupluUrunler && grupluUrunler.length > 0); const hasKirtasiye = !!(products && products.length > 0); return hasTextil !== hasKirtasiye ? 'single-tab' : '' })()}
        items={[
          ...(grupluUrunler && grupluUrunler.length > 0
            ? [
                {
                  key: '1',
                  label: (
                    <>
                      <SchoolIcon />
                      OKUL KIYAFETİ
                    </>
                  ),
                  children: (
                    <SchoolClothesPage
                      selectedProducts={selectedProducts}
                      setSelectedProducts={setSelectedProducts}
                      grupluUrunler={grupluUrunler}
                    />
                  ),
                },
              ]
            : []),
          ...(products && products.length > 0
            ? [
                {
                  key: '2',
                  label: (
                    <>
                      <ClassIcon />
                      {kirtasiyeTabLabel}
                    </>
                  ),
                  children: (
                    <div>
                      <div className="select-all-container">
                        <Button type="primary" className="select-all-btn" onClick={handleToggleSelectAll}>
                          {isAllSelected ? 'Tüm Seçimi Kaldır' : 'Tümünü Seç'}
                        </Button>
                      </div>
                      <div className="ders-bloklari">
                        {collapseItems.map((item) => (
                          <div
                            key={item.key}
                            className="ders-blok"
                            style={{ '--ders-renk': item.dersRenk } as React.CSSProperties}
                          >
                            <div className="ders-etiket-dikey">
                              <span className="vertical-text">{item.dersIsim}</span>
                            </div>
                            {item.content}
                          </div>
                        ))}
                      </div>
                    </div>
                  ),
                },
              ]
            : []),
        ]}
      />
      <ClassProductsModal
        modelVisible={modalVisible}
        setModalVisible={setModalVisible}
        selectedProducts={selectedProducts}
        setSelectedProducts={setSelectedProducts}
      />
      <Modal
        title={variantGroupProducts[0]?.urun?.grup?.isim || 'Varyant Seçiniz'}
        open={variantModalVisible}
        onCancel={() => setVariantModalVisible(false)}
        footer={null}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
          {variantGroupProducts.map((product) => {
            const variantLabel =
              product.urun?.bilgiler?.find((b) => b?.isim === 'Beden')?.deger || product.urun?.isim || ''
            const dersId = product.ders?.id!
            const grupId = product.urun?.grup?.id ?? null
            const isSelected =
              selectedProducts.some((sp) => sp.urunId === product.urun_id && sp.dersId === dersId && sp.grupId === grupId)
            return (
              <div
                key={product.urun_id}
                onClick={() => handleGroupVariantSelect(product)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 14px',
                  border: `2px solid ${isSelected ? 'var(--color-primary, #1677ff)' : '#e0e0e0'}`,
                  borderRadius: 8,
                  cursor: 'pointer',
                  background: isSelected ? 'color-mix(in srgb, var(--color-primary, #1677ff), #fff 90%)' : 'white',
                }}
              >
                <div onClick={(e) => e.stopPropagation()}>
                  <Image
                    src={product.urun?.resimler?.[0] || ''}
                    alt={variantLabel}
                    width={64}
                    height={64}
                    style={{ objectFit: 'contain' }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>Beden {variantLabel}</div>
                  <div style={{ fontSize: 13, color: '#555' }}>
                    {setPriceFormat(String(product.indirimli_birim_fiyat), 1)} TL
                  </div>
                  {(() => {
                    const EXCLUDED = new Set(['StkID', 'Genel', 'Beden', 'Okul İndirimi Uygulanır', 'Kargo Bedava', 'Okula T.', 'TC Gerektiren Ürün', 'Dönüşüm Ürünü', 'Kitap Kaplama Adeti', 'Açıklama'])
                    const olculer = (product.urun?.bilgiler ?? []).filter((b) => b && !EXCLUDED.has(b.isim ?? ''))
                    if (olculer.length === 0) return null
                    return (
                      <div style={{ fontSize: 12, color: '#777', marginTop: 4 }}>
                        {olculer.map((b) => `${b!.isim}: ${b!.deger}`).join(' | ')}
                      </div>
                    )
                  })()}
                </div>
              </div>
            )
          })}
        </div>
      </Modal>
    </div>
  )
}
